import { useRef, useState } from 'react';
import { markdownToHtml } from '@epasu/core/richtext';
import { Bold, Eye, Italic, Link2, List, ListOrdered, Pencil } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  label?: string;
}

/**
 * Textfeld mit Formatierung.
 *
 * Geschrieben wird weiterhin Klartext mit einer schlanken Auszeichnung
 * (`**fett**`, `- Stichpunkt`); die Knöpfe setzen sie um die Auswahl. Beim
 * Versand entsteht daraus HTML.
 *
 * Bewusst kein WYSIWYG-Feld: Der gespeicherte Text bleibt lesbar, und
 * Kundenakte, Vorschau und Suche arbeiten unverändert weiter — sie alle gehen
 * von Klartext aus. Damit trotzdem sichtbar ist, was ankommt, gibt es die
 * Vorschau.
 */
export function RichTextArea({
  value,
  onChange,
  placeholder,
  rows = 14,
  label,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [vorschau, setVorschau] = useState(false);

  /**
   * Legt Zeichen um die Auswahl.
   *
   * Ist nichts ausgewählt, wird ein Platzhalter eingefügt und markiert — sonst
   * müsste man erst tippen und dann noch einmal auswählen.
   */
  const umschliessen = (marke: string, platzhalter: string) => {
    const feld = ref.current;
    if (!feld) return;

    const { selectionStart: von, selectionEnd: bis } = feld;
    const auswahl = value.slice(von, bis) || platzhalter;
    const neu = `${value.slice(0, von)}${marke}${auswahl}${marke}${value.slice(bis)}`;

    onChange(neu);
    // Nach dem Neuzeichnen die eingefügte Stelle markieren.
    requestAnimationFrame(() => {
      feld.focus();
      feld.setSelectionRange(von + marke.length, von + marke.length + auswahl.length);
    });
  };

  /** Stellt jeder ausgewählten Zeile ein Zeichen voran. */
  const zeilenPraefix = (praefix: (index: number) => string) => {
    const feld = ref.current;
    if (!feld) return;

    const { selectionStart: von, selectionEnd: bis } = feld;
    const zeilenAnfang = value.lastIndexOf('\n', von - 1) + 1;
    const zeilenEndeRoh = value.indexOf('\n', bis);
    const zeilenEnde = zeilenEndeRoh === -1 ? value.length : zeilenEndeRoh;

    const block = value.slice(zeilenAnfang, zeilenEnde) || 'Punkt';
    const umgebaut = block
      .split('\n')
      .map((zeile, i) => {
        // Schon ausgezeichnete Zeilen nicht doppelt bepacken.
        const blank = zeile.replace(/^\s*([-*•]|\d+[.)])\s+/, '');
        return `${praefix(i)}${blank}`;
      })
      .join('\n');

    onChange(value.slice(0, zeilenAnfang) + umgebaut + value.slice(zeilenEnde));
    requestAnimationFrame(() => {
      feld.focus();
      feld.setSelectionRange(zeilenAnfang, zeilenAnfang + umgebaut.length);
    });
  };

  const einfuegenLink = () => {
    const feld = ref.current;
    if (!feld) return;
    const { selectionStart: von, selectionEnd: bis } = feld;
    const text = value.slice(von, bis) || 'Beschriftung';
    const neu = `${value.slice(0, von)}[${text}](https://)${value.slice(bis)}`;

    onChange(neu);
    requestAnimationFrame(() => {
      feld.focus();
      // Direkt hinter `https://` landen — dort tippt man weiter.
      const pos = von + text.length + 3 + 'https://'.length;
      feld.setSelectionRange(pos, pos);
    });
  };

  /** Strg/Cmd+B und Strg/Cmd+I, wie in jedem Editor. */
  const tastatur = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    if (e.key === 'b') {
      e.preventDefault();
      umschliessen('**', 'fett');
    } else if (e.key === 'i') {
      e.preventDefault();
      umschliessen('*', 'kursiv');
    }
  };

  return (
    <div className="space-y-1.5">
      {label && (
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      )}

      <div className="rounded-md border border-input">
        <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 p-1">
          <Knopf
            titel="Fett (Strg+B)"
            onClick={() => umschliessen('**', 'fett')}
            disabled={vorschau}
          >
            <Bold className="h-4 w-4" />
          </Knopf>
          <Knopf
            titel="Kursiv (Strg+I)"
            onClick={() => umschliessen('*', 'kursiv')}
            disabled={vorschau}
          >
            <Italic className="h-4 w-4" />
          </Knopf>

          <span className="mx-1 h-4 w-px bg-border" />

          <Knopf
            titel="Stichpunkte"
            onClick={() => zeilenPraefix(() => '- ')}
            disabled={vorschau}
          >
            <List className="h-4 w-4" />
          </Knopf>
          <Knopf
            titel="Nummerierte Liste"
            onClick={() => zeilenPraefix((i) => `${i + 1}. `)}
            disabled={vorschau}
          >
            <ListOrdered className="h-4 w-4" />
          </Knopf>
          <Knopf titel="Link" onClick={einfuegenLink} disabled={vorschau}>
            <Link2 className="h-4 w-4" />
          </Knopf>

          <span className="ml-auto" />

          <Knopf
            titel={vorschau ? 'Weiter schreiben' : 'Vorschau — so kommt es an'}
            onClick={() => setVorschau((v) => !v)}
            aktiv={vorschau}
          >
            {vorschau ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span className="ml-1.5 text-xs">
              {vorschau ? 'Bearbeiten' : 'Vorschau'}
            </span>
          </Knopf>
        </div>

        {vorschau ? (
          <div
            className="mail-vorschau min-h-[8rem] px-3 py-2 text-sm"
            style={{ minHeight: `${rows * 1.4}rem` }}
            // Der HTML-Text stammt aus markdownToHtml, und das maskiert alle
            // Sonderzeichen der Eingabe, bevor es Auszeichnung setzt. Was hier
            // ankommt, kann also nur aus den erlaubten Elementen bestehen.
            dangerouslySetInnerHTML={{ __html: markdownToHtml(value) }}
          />
        ) : (
          <textarea
            ref={ref}
            className="w-full resize-y bg-background px-3 py-2 text-sm outline-none"
            style={{ minHeight: `${rows * 1.4}rem` }}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={tastatur}
          />
        )}
      </div>

      <span className="text-xs text-muted-foreground">
        <code>**fett**</code>, <code>*kursiv*</code>, <code>- Stichpunkt</code> —
        die Knöpfe setzen das um die Auswahl.
      </span>
    </div>
  );
}

function Knopf({
  titel,
  onClick,
  disabled,
  aktiv,
  children,
}: {
  titel: string;
  onClick: () => void;
  disabled?: boolean;
  aktiv?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={titel}
      aria-label={titel}
      aria-pressed={aktiv}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center rounded px-1.5 py-1 transition-colors disabled:opacity-40 ${
        aktiv ? 'bg-background shadow-sm' : 'hover:bg-background'
      }`}
    >
      {children}
    </button>
  );
}
